/**
 * /vendors — listado de proveedores.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Building2, ChevronRight, Plus, Search } from 'lucide-react'
import { PageHeader } from '@/components/app-shell'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { StatusPill } from '@/components/ui/status-pill'
import { listVendors } from '@/lib/api'

export function VendorsPage() {
  const [q, setQ] = useState('')
  const query = useQuery({
    queryKey: ['vendors', q],
    queryFn: () => listVendors({ q: q || undefined }),
  })
  const rows = query.data?.vendors ?? []

  return (
    <>
      <PageHeader
        title="Vendors"
        subtitle={`${rows.length} proveedor${rows.length === 1 ? '' : 'es'}`}
        actions={
          <Link
            to="/vendors/new"
            className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="size-4" aria-hidden />
            Nuevo
          </Link>
        }
      />

      <div className="mb-4">
        <label className="relative block max-w-md">
          <span className="sr-only">Buscar</span>
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Empresa, contacto, teléfono…"
            className="h-11 w-full rounded-xl border border-border bg-card pr-3 pl-10 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={Building2}
            title="Sin proveedores"
            description="Crea el primero con el botón Nuevo, o carga la semilla del Excel."
          />
        </Card>
      ) : (
        <Card className="divide-y divide-border overflow-hidden">
          {rows.map((v) => (
            <Link
              key={v.id}
              to="/vendors/$vendorId/edit"
              params={{ vendorId: v.id }}
              className="flex min-h-[60px] items-center gap-3 px-4 py-3 transition-colors hover:bg-muted"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{v.companyName}</span>
                <span className="block text-xs text-muted-foreground">
                  {[v.contactName, v.phone, v.city].filter(Boolean).join(' · ') || 'Sin contacto'}
                </span>
              </span>
              <StatusPill status={v.vendorType} tone="info" />
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            </Link>
          ))}
        </Card>
      )}
    </>
  )
}
